import * as React from "react"
import { MdSettingsEthernet } from "react-icons/md"
import { Badge, Theme } from "gatsby-interface"

type IntegrationProps = {
  children: React.ReactNode
  large?: boolean
  className?: string
}

export function Integration({
  children,
  large = false,
  className,
}: IntegrationProps) {
  return (
    <div
      css={theme => ({
        padding: large
          ? `${theme.space[8]} ${theme.space[9]} ${theme.space[9]}`
          : `${theme.space[5]} ${theme.space[9]}`,
      })}
      className={className}
    >
      {children}
    </div>
  )
}

const rowCss = (_theme: Theme) => ({
  display: "flex",
  flexDirection: "row",
  flex: 1,
})

Integration.Row = function({ children }: { children: React.ReactNode }) {
  return <div css={rowCss}>{children}</div>
}

const vendorImageCss = (theme: Theme) => ({
  height: `12px`,
  width: `auto`,
  margin: 0,
  display: "flex",
  alignSelf: "center",
  [theme.mediaQueries.tablet]: {
    height: `24px`,
  },
})

type IntegrationVendorProps = {
  image: string
  name: string
}

Integration.Vendor = function({ image, name }: IntegrationVendorProps) {
  return <img src={image} alt={name} css={vendorImageCss} />
}

const statusCss = (theme: Theme) => ({
  marginLeft: theme.space[5],
  justifyContent: "center",
  display: "flex",
})

type IntegrationStatusProps = {
  connected: boolean
}

Integration.Status = function({ connected, ...props }: IntegrationStatusProps) {
  return connected ? (
    <Badge
      Icon={MdSettingsEthernet}
      css={statusCss}
      {...props}
      tone={"SUCCESS"}
    >
      Connected
    </Badge>
  ) : null
}

const actionsCss = {
  marginLeft: "auto",
  display: "flex",
  alignSelf: "center",
}

Integration.Actions = function({ children }: { children: React.ReactNode }) {
  return <div css={actionsCss}>{children}</div>
}

const detailsCss = (theme: Theme) => ({
  marginBottom: 0,
  marginTop: theme.space[5],
})

Integration.Details = function({
  children,
  ...props
}: {
  children: React.ReactNode
}) {
  return (
    <dl css={detailsCss} {...props}>
      {children}
    </dl>
  )
}

const detailCss = (theme: Theme) => ({
  display: `grid`,

  fontSize: theme.fontSizes[1],
  gridTemplateColumns: `1fr 1fr`,
  marginTop: theme.space[4],
  "&:first-of-type": {
    marginTop: `0`,
  },

  [theme.mediaQueries.tablet]: {
    gridTemplateColumns: `0.35fr 1fr`,
  },
})

Integration.Detail = function({
  children,
  ...props
}: {
  children: React.ReactNode
}) {
  return (
    <div {...props} css={detailCss}>
      {children}
    </div>
  )
}

const labelCss = (theme: Theme) => ({
  fontFamily: theme.fonts.body,
  fontWeight: 100,
  lineHeight: theme.lineHeights.heading,
  textTransform: `capitalize`,
  display: "flex",
  alignSelf: "center",
  marginBottom: 0,
})

Integration.Label = function({ children }: { children: React.ReactNode }) {
  return <dd css={labelCss}>{children}</dd>
}

const valueCss = (theme: Theme) => ({
  marginTop: 0,
  marginBottom: 0,
  color: theme.colors.grey[90],
})

Integration.Value = function({ children }: { children: React.ReactNode }) {
  return <dd css={valueCss}>{children}</dd>
}
